/**
 * Calendar view resolution (NOT-322).
 *
 * Precedence when the calendar opens: the URL `?view` pin wins; otherwise the
 * tenant's Default calendar view; otherwise `week`. There is intentionally no
 * per-user "last view" — a fresh `/dashboard/calendar` always honors the tenant
 * default.
 */
export type CalendarViewType = 'week' | 'workweek' | 'month' | 'list' | 'board' | 'technician';

export const CALENDAR_VIEW_TYPES: readonly CalendarViewType[] = [
  'week',
  'workweek',
  'month',
  'list',
  'board',
  'technician',
];

/**
 * Resolve a tenant `defaultCalendarView` string to a renderable view.
 *
 * `technician` is intentionally excluded here: it's a valid `?view` URL pin (see
 * CALENDAR_VIEW_TYPES) but NOT a persisted tenant default — the BusinessSettings
 * contract (office-settings schema/options, overview labels) only knows
 * week|workweek|month|list|board, so allowing it as a default couldn't round-trip.
 */
export function resolveDefaultView(view: string | undefined): CalendarViewType {
  return view === 'workweek' || view === 'month' || view === 'list' || view === 'board'
    ? view
    : 'week';
}

/**
 * The view the calendar should open to: URL `?view` > tenant default > `week`.
 * `searchView` is the already-validated `?view` pin (or undefined when absent).
 */
export function resolveCalendarView(
  searchView: CalendarViewType | undefined,
  tenantDefault: string | undefined,
): CalendarViewType {
  return searchView ?? resolveDefaultView(tenantDefault);
}
